// src/data/arbitraries.js
//
// Item 27 / master list 5.8 / ISS-31 (2026-10-16). Bob: "Need an area to display, calculate, add, edit, delete
// arbitraries." An arbitrary is a fixed allowance owed for defined work or conditions; it sits beside the rate, not inside
// it - a third input to a valuation (rate, hours, arbitraries). Per agreement, never across (Bob's doctrine: no
// cross-contamination between agreements or committees). Each row carries the provision it comes from and its effective
// date, exactly as a rate does (ISS-29 rule 1).
//
// DELETE MEANS RETIRE (Bob, 2026-08-28, ISS-31: "agreed, make it so"): a row stops applying from its retirement date
// forward, and every claim already valued on it keeps its figure and can still show where it came from. A retired row
// stays visible. EDIT is therefore retire-and-replace: the platform never rewrites an amount that may already stand
// behind a claim's arithmetic (ruling 30 - segregate, do not destroy).
//
// Stored one document per row at arbitraries/{committee}/rows/{rowId}; written by the committee's General Chairperson (or
// the administrator); read by any provisioned account. firestore.rules holds the shape and allows exactly one update to a
// row - its retirement. No delete.
//
// PURE. No Firestore, no clock.

export const UNITS = Object.freeze({
  per_day: 'per day',
  per_shift: 'per shift (tour of duty)',
  per_hour: 'per hour',
  per_occurrence: 'per occurrence',
  per_month: 'per month',
});
export const MAX_AMOUNT = 100000;   // dollars; a guard against a typo, not a judgement of any allowance
export const MAX_TEXT = 400;

const ISO = /^\d{4}-\d{2}-\d{2}$/;
const s = (v) => (v == null ? '' : String(v)).trim();
function validDate(d) {
  if (!ISO.test(d)) return false;
  const t = new Date(d + 'T12:00:00Z');
  return !Number.isNaN(t.getTime()) && t.toISOString().slice(0, 10) === d;
}
export function agreementSlug(name) { return s(name).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 120); }
export const money = (n) => '$' + Number(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

// A new row, validated. input: { committee, agreementName, name, unit, amount, effectiveFrom, provision, sourceDoc }.
// `agreements` = the committee's agreement names (serviceSettings.committeeAgreements) - a row names one of them.
export function planArbitrary(input, agreements) {
  const i = input || {};
  const errors = [];
  const names = Array.isArray(agreements) ? agreements : [];
  if (!s(i.committee)) errors.push('No committee.');
  if (!names.includes(s(i.agreementName))) errors.push('Choose the agreement this allowance belongs to - one of the committee\'s own.');
  if (!s(i.name)) errors.push('Name the allowance as the agreement names it.');
  if (s(i.name).length > MAX_TEXT) errors.push('The name is at most ' + MAX_TEXT + ' characters.');
  if (!UNITS[i.unit]) errors.push('Say how it is paid: ' + Object.values(UNITS).join(', ') + '.');
  const amt = Number(s(i.amount));
  if (!s(i.amount) || !Number.isFinite(amt) || amt <= 0 || amt > MAX_AMOUNT || Math.round(amt * 100) !== amt * 100) {
    errors.push('The amount is dollars and cents, more than 0 and at most ' + money(MAX_AMOUNT) + '.');
  }
  if (!validDate(s(i.effectiveFrom))) errors.push('The date it is effective from (YYYY-MM-DD) is required - an undated allowance cannot be applied to a claim date.');
  if (!s(i.provision)) errors.push('Give the provision it comes from (article, rule, section or side letter, and the line) - no allowance enters without its source.');
  if (s(i.provision).length > MAX_TEXT || s(i.sourceDoc).length > MAX_TEXT) errors.push('The provision and the source document are at most ' + MAX_TEXT + ' characters each.');
  if (errors.length) return { ok: false, errors };
  return { ok: true, errors: [], row: {
    committee: s(i.committee), agreementName: s(i.agreementName), agreementSlug: agreementSlug(i.agreementName),
    name: s(i.name), unit: i.unit, amount: amt, effectiveFrom: s(i.effectiveFrom),
    provision: s(i.provision), sourceDoc: s(i.sourceDoc) || null,
    retiredOn: null, retireReason: null,
  } };
}

// Retirement, validated: the date it stops applying (the first day it no longer applies) and the reason.
export function planRetire(row, input) {
  const r = row || {}, i = input || {};
  const errors = [];
  if (r.retiredOn) errors.push('This allowance was already retired on ' + r.retiredOn + '. A retirement is not changed; add a new row if it applies again.');
  if (!validDate(s(i.retiredOn))) errors.push('The date it stops applying (YYYY-MM-DD) is required.');
  else if (validDate(s(r.effectiveFrom)) && s(i.retiredOn) <= r.effectiveFrom) errors.push('It cannot stop applying on or before the day it began (' + r.effectiveFrom + ').');
  if (!s(i.reason)) errors.push('Give the reason - a superseding agreement, a correction, or the provision that ended it.');
  if (s(i.reason).length > MAX_TEXT) errors.push('The reason is at most ' + MAX_TEXT + ' characters.');
  if (errors.length) return { ok: false, errors };
  return { ok: true, errors: [], patch: { retiredOn: s(i.retiredOn), retireReason: s(i.reason) } };
}

// Does a row apply on a date (YYYY-MM-DD)? From effectiveFrom inclusive to retiredOn exclusive.
export function appliesOn(row, date) {
  const r = row || {};
  return validDate(s(date)) && s(r.effectiveFrom) <= date && (!r.retiredOn || date < r.retiredOn);
}

// The row of that name, for that agreement, that applies on the date. Two rows of one name live on one date is a
// conflict in the record and is said, never resolved by picking one.
export function arbitraryOn(rows, agreementName, name, date) {
  const slug = agreementSlug(agreementName), n = s(name).toLowerCase();
  const live = (Array.isArray(rows) ? rows : []).filter(r => r && r.agreementSlug === slug && s(r.name).toLowerCase() === n && appliesOn(r, date));
  if (live.length === 1) return { ok: true, row: live[0] };
  if (!live.length) return { ok: false, row: null, why: 'No "' + s(name) + '" allowance of ' + s(agreementName) + ' applies on ' + date + '.' };
  return { ok: false, row: null, why: live.length + ' rows named "' + s(name) + '" apply on ' + date + ' - the record conflicts; retire the wrong one.' };
}

// The calculation: the row's amount times the units claimed, shown with its work. units: a whole number of days,
// shifts, occurrences or months; hours to the quarter.
export function calculate(row, units) {
  const r = row || {};
  const u = Number(units);
  const step = r.unit === 'per_hour' ? 0.25 : 1;
  if (!UNITS[r.unit] || !Number.isFinite(Number(r.amount))) return { ok: false, why: 'Not an allowance on record.' };
  if (!Number.isFinite(u) || u <= 0 || Math.round(u / step) * step !== u) {
    return { ok: false, why: 'Units must be ' + (step === 1 ? 'a whole number' : 'hours to the quarter') + ', more than 0.' };
  }
  const total = Math.round(Number(r.amount) * u * 100) / 100;
  return { ok: true, total, work: money(r.amount) + ' ' + UNITS[r.unit] + ' x ' + u + ' = ' + money(total)
    + ' (' + r.provision + (r.sourceDoc ? ', ' + r.sourceDoc : '') + '; effective ' + r.effectiveFrom + (r.retiredOn ? ', retired ' + r.retiredOn : '') + ')' };
}

// The display order: by agreement, then name, then effective date - live rows and retired rows together, retired marked.
export function sortRows(rows) {
  return (Array.isArray(rows) ? rows.filter(Boolean) : []).slice().sort((a, b) =>
    s(a.agreementName).localeCompare(s(b.agreementName)) || s(a.name).localeCompare(s(b.name)) || (s(a.effectiveFrom) < s(b.effectiveFrom) ? -1 : 1));
}
